/**
 * What the wall tablet leads with, and where everything else goes (D-914).
 *
 * `asked`, the owner, after the first tablet went up: *"its a bit overwhelming
 * to look at it initially and find the class that you're looking for."* Nine
 * equal rows in the Black Room on a Monday, then twenty-nine more below them.
 *
 * **Pure, because the partition is the part that goes quietly wrong.** The
 * first draft of this design had *on now* and *next* as cards and only an
 * *earlier* button, which left a class later today but not next **reachable
 * from nothing** — on the screen whose only job is opening registers. A red
 * team found it in the arithmetic of the spec's own example, which is exactly
 * the sort of hole a browser test would not have shown.
 */

import { currentSession, minutes, standing } from './dancers'
import type { SessionOption } from './attendance'

/** Everything the screen needs, already bucketed. */
export type RoomDay = {
  /**
   * The class in front of her: running now, else the next one.
   *
   * **Null when there is nothing to lead with**, and that is three cases
   * rather than one — the day is over, the day is not today, or the device is
   * in no room at all. The last is the D-116 escape hatch: a spare tablet
   * claiming a room it is not in would be a lie, so it falls back to the plain
   * list the screen has always drawn.
   */
  hero: SessionOption | null
  /** `now` when the hero is running, `next` when it is still to come. */
  heroIs: 'now' | 'next' | null
  /** The one after the hero, shown only when the hero is actually running. */
  next: SessionOption | null
  earlierHere: SessionOption[]
  laterHere: SessionOption[]
  /**
   * Earlier classes in this room that nobody opened a register for.
   *
   * `asked`: *"showing all of the ones from earlier with the ones that didn't
   * get attendance taken might make the most sense to a teacher trying to go
   * fill in something they forgot."*
   *
   * ⚠ **Not the same as unfinished.** A register with some marks and some gaps
   * is a judgement call about one dancer; one nobody opened is the thing she is
   * going back for. And a **room hold has no register to take at all** (D-80),
   * so it is never counted — the screen already refuses to say *0 of 0 marked*
   * about one.
   */
  untakenEarlier: SessionOption[]
  /**
   * This room's classes are all finished — the one no-hero case that still
   * gets the three buttons.
   *
   * ⚠ **Not a synonym for `hero === null`.** The other no-hero cases are a day
   * that is not today and a device in no room, where *Earlier today in this
   * room* would be a claim about the wrong day or about a room the tablet is
   * not in. **This is the case the owner's words are actually about** — *"a
   * teacher trying to go fill in something they forgot"* is a teacher doing it
   * after her classes — and for one commit it was the case that fell back to
   * the nine equal rows he had asked us to get rid of, with the untaken count
   * nowhere on screen. Found by an audit.
   *
   * False when nothing here has a start time (D-84): those classes are not
   * finished, they are unplaceable, and *"that's everything for today"* about
   * a class awaiting a slot is a different wrong answer.
   */
  dayIsOver: boolean
}

/** A room hold is a reservation, not a lesson (D-80), so it owes no register. */
function owesARegister(session: SessionOption): boolean {
  return session.enrolled > 0
}

export function roomDay(
  here: readonly SessionOption[],
  nowMinutes: number,
  { isToday, hasRoom }: { isToday: boolean; hasRoom: boolean },
): RoomDay {
  const empty: RoomDay = {
    hero: null,
    heroIs: null,
    next: null,
    earlierHere: [],
    laterHere: [],
    untakenEarlier: [],
    dayIsOver: false,
  }

  if (!isToday || !hasRoom) return empty

  /*
   * ⚠ **The hero is `currentSession`'s answer, and for one commit it was a
   * transcribed copy of it** — sitting ten lines under a comment claiming the
   * opposite, and written *after* `currentSession` had been made generic
   * specifically so it could be used here. It was the objection the spec's own
   * red team raised and the spec answered, and the drift had already started:
   * the copy read an hour with `slice(0, 2)` where `minutes()` splits on the
   * colon, so an unpadded `9:30:00` was `NaN` in one and `570` in the other.
   * Found by an audit. (A-17)
   */
  const startsAt = (s: SessionOption) =>
    s.startTime ? minutes(s.startTime) : Number.POSITIVE_INFINITY

  const ordered = [...here].sort((a, b) => startsAt(a) - startsAt(b))
  const where = (s: SessionOption) => standing(s, nowMinutes, isToday)

  const hero = currentSession(ordered, nowMinutes, isToday)

  /*
   * **Computed once, on one path.** The hero and the next are never *earlier*
   * by construction, so promoting them takes nothing out of this bucket — which
   * means both branches below want the same two lines.
   *
   * ⚠ **They did not get them.** The over-for-the-day branch spread an empty
   * record and never computed the untaken count, so *"2 not taken"* read zero
   * from the moment the last class ended — which is **exactly when a teacher
   * comes back to fill in the one she forgot**, and the reason the owner asked
   * for the count at all. Found by a mutation check: making a room hold owe a
   * register changed nothing, because nothing was being counted.
   */
  const earlierHere = ordered.filter((s) => where(s) === 'earlier')
  const untakenEarlier = earlierHere.filter((s) => owesARegister(s) && s.marked === 0)

  if (hero === null) {
    // The evening is over, or nothing here has a time it can be judged by
    // (D-84). Everything stays reachable; nothing is promoted.
    return {
      ...empty,
      earlierHere,
      laterHere: ordered.filter((s) => where(s) !== 'earlier'),
      untakenEarlier,
      // Finished, not unplaceable: every class here is behind us.
      dayIsOver: ordered.length > 0 && earlierHere.length === ordered.length,
    }
  }

  const heroIs = where(hero) === 'now' ? 'now' : 'next'

  /*
   * **The same question asked of a shorter list.** `currentSession` answers
   * *running now, else up next* — so with everything running removed it
   * answers *up next*, and this is not a second copy of the rule either.
   *
   * Asked only when the hero is genuinely running: promoted, the hero **is**
   * the next class, and a card beside it would be itself twice.
   */
  const next =
    heroIs === 'now'
      ? currentSession(ordered.filter((s) => where(s) !== 'now'), nowMinutes, isToday)
      : null

  const promoted = new Set([hero.occurrenceId, next?.occurrenceId].filter(Boolean))

  return {
    hero,
    heroIs,
    next,
    earlierHere,
    /*
     * **Everything not earlier and not promoted**, which is what stops a class
     * later today from falling out of the screen altogether — the hole the
     * first draft of this design had, where the room's only button was
     * *earlier* and a class after next was reachable from nothing.
     */
    laterHere: ordered.filter((s) => where(s) !== 'earlier' && !promoted.has(s.occurrenceId)),
    untakenEarlier,
    // There is a class to lead with, so the day is not over by definition.
    dayIsOver: false,
  }
}
